import { useState, useEffect } from "react";
import {
  Activity,
  Plus,
  Play,
  Pause,
  Trash2,
  Database,
  Cloud,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Zap,
  ArrowRight,
  BarChart3,
  Table,
  Layers,
  Clock,
  X,
  Info,
  Shield,
  Wifi,
  WifiOff,
  Timer,
  HardDrive,
  Check,
  ChevronDown,
  ChevronUp,
  FileText,
  AlertTriangle,
  Globe,
  Building2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";
import api from "@/services/api";

const StatusBadge = ({ status }) => {
  const config = {
    running: { icon: Activity, label: "Running", className: "bg-green-100 text-green-700 border-green-200" },
    created: { icon: CheckCircle2, label: "Created", className: "bg-blue-100 text-blue-700 border-blue-200" },
    paused: { icon: Pause, label: "Paused", className: "bg-yellow-100 text-yellow-700 border-yellow-200" },
    pending: { icon: Loader2, label: "Pending", className: "bg-gray-100 text-gray-700 border-gray-200" },
    creating: { icon: Loader2, label: "Creating", className: "bg-blue-100 text-blue-700 border-blue-200 animate-pulse" },
    error: { icon: AlertCircle, label: "Error", className: "bg-red-100 text-red-700 border-red-200" },
    active: { icon: CheckCircle2, label: "Active", className: "bg-green-100 text-green-700 border-green-200" },
    completed: { icon: CheckCircle2, label: "Completed", className: "bg-green-100 text-green-700 border-green-200" },
    failed: { icon: AlertCircle, label: "Failed", className: "bg-red-100 text-red-700 border-red-200" },
  };

  const { icon: Icon, label, className } = config[status] || config.pending;

  return (
    <Badge variant="outline" className={className}>
      <Icon className={`w-3 h-3 mr-1 ${status === 'creating' || status === 'running' ? 'animate-spin' : ''}`} />
      {label}
    </Badge>
  );
};

const MethodBadge = ({ method }) => {
  const config = {
    datastream: { label: "Datastream", className: "bg-purple-100 text-purple-700 border-purple-200", icon: Zap },
    timestamp_polling: { label: "Timestamp Polling", className: "bg-blue-100 text-blue-700 border-blue-200", icon: Timer },
    mview_log: { label: "MView Logs", className: "bg-orange-100 text-orange-700 border-orange-200", icon: FileText },
  };

  const { label, className, icon: Icon } = config[method] || { label: method, className: "bg-gray-100 text-gray-700", icon: Activity };

  return (
    <Badge variant="outline" className={className}>
      <Icon className="w-3 h-3 mr-1" />
      {label}
    </Badge>
  );
};

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// CDC Method Selection Card Component
const CDCMethodCard = ({ method, selected, onSelect }) => {
  const icons = {
    datastream: Zap,
    timestamp_polling: Timer,
    mview_log: FileText,
  };
  
  const colors = {
    datastream: "from-purple-500 to-indigo-500",
    timestamp_polling: "from-blue-500 to-cyan-500",
    mview_log: "from-orange-500 to-amber-500",
  };
  
  const Icon = icons[method.id] || Activity;
  const gradient = colors[method.id] || "from-gray-500 to-gray-600";
  
  return (
    <Card 
      className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
        selected ? 'ring-2 ring-primary ring-offset-2' : 'hover:border-primary/50'
      }`}
      onClick={() => onSelect(method.id)}
      data-testid={`cdc-method-${method.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient} text-white`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{method.name}</CardTitle>
              {selected && (
                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
            <CardDescription className="text-xs mt-1">{method.short_description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3 text-muted-foreground" />
            <span>{method.latency}</span>
          </div>
          <div className="flex items-center gap-1">
            <HardDrive className="w-3 h-3 text-muted-foreground" />
            <span>{method.performance_impact} impact</span>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-1">
          {method.id === 'datastream' && (
            <>
              <Badge variant="secondary" className="text-[10px] gap-1 bg-green-50 text-green-700">
                <Check className="w-2.5 h-2.5" /> Real-time
              </Badge>
              <Badge variant="secondary" className="text-[10px] gap-1 bg-green-50 text-green-700">
                <Check className="w-2.5 h-2.5" /> DELETEs
              </Badge>
              <Badge variant="secondary" className="text-[10px] gap-1 bg-red-50 text-red-700">
                <WifiOff className="w-2.5 h-2.5" /> Needs Firewall Access
              </Badge>
              <Badge variant="secondary" className="text-[10px] gap-1 bg-yellow-50 text-yellow-700">
                <Globe className="w-2.5 h-2.5" /> UTF-8 Only
              </Badge>
            </>
          )}
          {method.id === 'timestamp_polling' && (
            <>
              <Badge variant="secondary" className="text-[10px] gap-1 bg-green-50 text-green-700">
                <Wifi className="w-2.5 h-2.5" /> Firewall Friendly
              </Badge>
              <Badge variant="secondary" className="text-[10px] gap-1 bg-green-50 text-green-700">
                <Globe className="w-2.5 h-2.5" /> All Encodings
              </Badge>
              <Badge variant="secondary" className="text-[10px] gap-1 bg-yellow-50 text-yellow-700">
                <AlertTriangle className="w-2.5 h-2.5" /> No DELETEs
              </Badge>
            </>
          )}
          {method.id === 'mview_log' && (
            <>
              <Badge variant="secondary" className="text-[10px] gap-1 bg-green-50 text-green-700">
                <Wifi className="w-2.5 h-2.5" /> Firewall Friendly
              </Badge>
              <Badge variant="secondary" className="text-[10px] gap-1 bg-green-50 text-green-700">
                <Globe className="w-2.5 h-2.5" /> All Encodings
              </Badge>
              <Badge variant="secondary" className="text-[10px] gap-1 bg-green-50 text-green-700">
                <Check className="w-2.5 h-2.5" /> DELETEs
              </Badge>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Detailed Method Info Component
const MethodDetailsPanel = ({ method, isOpen, onToggle }) => {
  if (!method) return null;
  
  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-between mb-2">
          <span className="flex items-center gap-2">
            <Info className="w-4 h-4" />
            View detailed comparison
          </span>
          {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="p-4 bg-muted/50 rounded-lg space-y-4 text-sm">
          {/* Unicode Support Section */}
          {method.unicode_support && (
            <div className={`p-3 rounded-lg border ${
              method.unicode_support.compatible 
                ? 'bg-green-50 border-green-200' 
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <h4 className={`font-semibold mb-2 flex items-center gap-2 ${
                method.unicode_support.compatible ? 'text-green-700' : 'text-yellow-700'
              }`}>
                <Globe className="w-4 h-4" />
                Character Encoding Support
              </h4>
              <p className="text-xs mb-2">{method.unicode_support.description}</p>
              <div className="flex flex-wrap gap-1 mb-2">
                {method.unicode_support.legacy_encodings?.slice(0, 4).map((enc, i) => (
                  <Badge key={i} variant="outline" className="text-[10px]">
                    {enc}
                  </Badge>
                ))}
              </div>
              <p className={`text-xs font-medium ${
                method.unicode_support.compatible ? 'text-green-600' : 'text-yellow-600'
              }`}>
                💡 {method.unicode_support.recommendation}
              </p>
            </div>
          )}
          
          <div>
            <h4 className="font-semibold text-green-700 mb-2">✓ Pros</h4>
            <ul className="space-y-1">
              {method.pros?.map((pro, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-3 h-3 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-xs">{pro}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-red-700 mb-2">✗ Cons</h4>
            <ul className="space-y-1">
              {method.cons?.map((con, i) => (
                <li key={i} className="flex items-start gap-2">
                  <X className="w-3 h-3 text-red-600 mt-1 flex-shrink-0" />
                  <span className="text-xs">{con}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-blue-700 mb-2">📋 Requirements</h4>
            <ul className="space-y-1">
              {method.requirements?.map((req, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Shield className="w-3 h-3 text-blue-600 mt-1 flex-shrink-0" />
                  <span className="text-xs">{req}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const CDCStreamCard = ({ stream, onStart, onPause, onDelete, onRefresh, onViewMetrics, onPollNow, loading }) => (
  <Card className="relative overflow-hidden" data-testid={`cdc-stream-${stream.id}`}>
    <div className={`absolute top-0 left-0 w-full h-1 ${
      stream.cdc_method === 'datastream' 
        ? 'bg-gradient-to-r from-purple-500 to-indigo-500'
        : stream.cdc_method === 'timestamp_polling'
        ? 'bg-gradient-to-r from-blue-500 to-cyan-500'
        : 'bg-gradient-to-r from-orange-500 to-amber-500'
    }`} />
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${
            stream.cdc_method === 'datastream' 
              ? 'bg-purple-100'
              : stream.cdc_method === 'timestamp_polling'
              ? 'bg-blue-100'
              : 'bg-orange-100'
          }`}>
            {stream.cdc_method === 'datastream' ? (
              <Zap className="w-5 h-5 text-purple-600" />
            ) : stream.cdc_method === 'timestamp_polling' ? (
              <Timer className="w-5 h-5 text-blue-600" />
            ) : (
              <FileText className="w-5 h-5 text-orange-600" />
            )}
          </div>
          <div>
            <CardTitle className="text-lg">{stream.name}</CardTitle>
            <CardDescription className="text-xs mt-1">
              {stream.destination_dataset} dataset
            </CardDescription>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <StatusBadge status={stream.status} />
          <MethodBadge method={stream.cdc_method} />
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Database className={`w-4 h-4 ${
          stream.source_type === 'sqlserver' ? 'text-blue-500' 
          : stream.source_type === 'postgresql' ? 'text-teal-500' 
          : 'text-orange-500'
        }`} />
        <span>{stream.source_type === 'sqlserver' ? 'SQL Server' : stream.source_type === 'postgresql' ? 'PostgreSQL' : 'Oracle'}</span>
        <ArrowRight className="w-4 h-4" />
        <Cloud className="w-4 h-4 text-blue-500" />
        <span>BigQuery</span>
      </div>

      {/* Polling-specific info */}
      {stream.cdc_method !== 'datastream' && (
        <div className="text-xs text-muted-foreground space-y-1">
          <div className="flex items-center gap-2">
            <Timer className="w-3 h-3" />
            <span>Poll interval: {stream.poll_interval_minutes} minutes</span>
          </div>
          {stream.last_poll_at && (
            <div className="flex items-center gap-2">
              <Clock className="w-3 h-3" />
              <span>Last poll: {new Date(stream.last_poll_at).toLocaleString()}</span>
            </div>
          )}
          {stream.total_rows_synced > 0 && (
            <div className="flex items-center gap-2">
              <Table className="w-3 h-3" />
              <span>Total synced: {stream.total_rows_synced.toLocaleString()} rows</span>
            </div>
          )}
        </div>
      )}

      {stream.error_message && (
        <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
          {stream.error_message}
        </div>
      )}

      {stream.started_at && (
        <div className="text-xs text-muted-foreground">
          Started: {new Date(stream.started_at).toLocaleString()}
        </div>
      )}

      <div className="flex gap-2 pt-2 flex-wrap">
        {stream.status === "created" || stream.status === "paused" ? (
          <Button
            variant="default"
            size="sm"
            onClick={() => onStart(stream.id)}
            disabled={loading === stream.id}
            data-testid={`start-stream-${stream.id}`}
          >
            {loading === stream.id ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-1" />
            )}
            Start
          </Button>
        ) : stream.status === "running" ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPause(stream.id)}
            disabled={loading === stream.id}
            data-testid={`pause-stream-${stream.id}`}
          >
            {loading === stream.id ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Pause className="w-4 h-4 mr-1" />
            )}
            Pause
          </Button>
        ) : null}

        {/* Poll Now button for polling methods */}
        {stream.cdc_method !== 'datastream' && stream.status === 'running' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPollNow(stream.id)}
            disabled={loading === stream.id}
            data-testid={`poll-now-${stream.id}`}
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Poll Now
          </Button>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewMetrics(stream)}
          data-testid={`view-metrics-${stream.id}`}
        >
          <BarChart3 className="w-4 h-4 mr-1" />
          Metrics
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRefresh(stream.id)}
          disabled={loading === stream.id}
        >
          <RefreshCw className={`w-4 h-4 ${loading === stream.id ? 'animate-spin' : ''}`} />
        </Button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive ml-auto">
              <Trash2 className="w-4 h-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete CDC Stream</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete &quot;{stream.name}&quot;? 
                {stream.cdc_method === 'datastream' && " This will also delete the associated Google Cloud Datastream resources."}
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onDelete(stream.id)}
                className="bg-destructive text-destructive-foreground"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </CardContent>
  </Card>
);

const OracleSourceCard = ({ source, onDelete }) => (
  <Card data-testid={`oracle-source-${source.id}`}>
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <Database className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <CardTitle className="text-lg">{source.name}</CardTitle>
            <CardDescription className="text-xs mt-1">
              {source.schemas.length} schema(s)
            </CardDescription>
          </div>
        </div>
        <StatusBadge status={source.status} />
      </div>
    </CardHeader>
    <CardContent>
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1">
          {source.schemas.map((schema) => (
            <Badge key={schema} variant="secondary" className="text-xs">
              {schema}
            </Badge>
          ))}
        </div>
        {source.tables && source.tables.length > 0 && (
          <div className="text-xs text-muted-foreground">
            {source.tables.length} specific table(s)
          </div>
        )}
      </div>
      <div className="flex justify-end mt-4">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
              <Trash2 className="w-4 h-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Oracle Source</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete &quot;{source.name}&quot;? Make sure no CDC streams are using this source.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onDelete(source.id)}
                className="bg-destructive text-destructive-foreground"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </CardContent>
  </Card>
);

// Generic CDC Source Card (for SQL Server, PostgreSQL)
const CDCSourceCard = ({ source, onDelete, sourceType }) => {
  const colorClass = sourceType === 'sqlserver' ? 'blue' : 'teal';
  const label = sourceType === 'sqlserver' ? 'SQL Server' : 'PostgreSQL';
  
  return (
    <Card data-testid={`cdc-source-${source.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 bg-${colorClass}-100 rounded-lg`}>
              <Database className={`w-5 h-5 text-${colorClass}-600`} />
            </div>
            <div>
              <CardTitle className="text-lg">{source.name}</CardTitle>
              <CardDescription className="text-xs mt-1">
                {label} • {source.schemas?.length || 0} schema(s)
              </CardDescription>
            </div>
          </div>
          <StatusBadge status={source.status} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1">
            {source.schemas?.map((schema) => (
              <Badge key={schema} variant="secondary" className="text-xs">
                {schema}
              </Badge>
            ))}
          </div>
          {source.tables && source.tables.length > 0 && (
            <div className="text-xs text-muted-foreground">
              {source.tables.length} specific table(s)
            </div>
          )}
        </div>
        <div className="flex justify-end mt-4">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {label} Source</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete &quot;{source.name}&quot;? Make sure no CDC streams are using this source.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onDelete(source.id)}
                  className="bg-destructive text-destructive-foreground"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
};

const TransformationCard = ({ transformation, onRunClick, onDelete, loading }) => (
  <Card data-testid={`transformation-${transformation.id}`}>
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${transformation.transformation_type === 'silver' ? 'bg-gray-100' : 'bg-yellow-100'}`}>
            <Layers className={`w-5 h-5 ${transformation.transformation_type === 'silver' ? 'text-gray-600' : 'text-yellow-600'}`} />
          </div>
          <div>
            <CardTitle className="text-lg">{transformation.name}</CardTitle>
            <CardDescription className="text-xs mt-1">
              {transformation.transformation_type.toUpperCase()} • {transformation.target_dataset}.{transformation.target_table}
            </CardDescription>
          </div>
        </div>
        <StatusBadge status={transformation.last_run_status || 'pending'} />
      </div>
    </CardHeader>
    <CardContent className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Table className="w-4 h-4" />
        <span>{transformation.source_table}</span>
        <ArrowRight className="w-4 h-4" />
        <span>{transformation.target_table}</span>
      </div>
      
      {/* Multi-tenant indicators */}
      <div className="flex gap-2 flex-wrap">
        {transformation.tenant_aware && (
          <Badge variant="outline" className="text-xs">
            <Building2 className="w-3 h-3 mr-1" />
            Tenant-Aware
          </Badge>
        )}
        {transformation.tenant_merge_strategy === 'merge_by_tenant' && (
          <Badge variant="secondary" className="text-xs">Merge by Tenant</Badge>
        )}
      </div>
      
      {transformation.last_run_at && (
        <div className="text-xs text-muted-foreground flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Last run: {new Date(transformation.last_run_at).toLocaleString()}
          {transformation.last_run_rows > 0 && ` (${transformation.last_run_rows.toLocaleString()} rows)`}
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button
          variant="default"
          size="sm"
          onClick={() => onRunClick(transformation)}
          disabled={loading === transformation.id}
          data-testid={`run-transformation-${transformation.id}`}
        >
          {loading === transformation.id ? (
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
          ) : (
            <Play className="w-4 h-4 mr-1" />
          )}
          Run for Tenant
        </Button>
        
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive ml-auto">
              <Trash2 className="w-4 h-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Transformation</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete &quot;{transformation.name}&quot;? This will not delete the target table in BigQuery.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onDelete(transformation.id)}
                className="bg-destructive text-destructive-foreground"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </CardContent>
  </Card>
);

const MetricsDialog = ({ stream, metrics, tables, open, onClose, loading }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
      <DialogHeader className="flex-shrink-0">
        <DialogTitle className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Stream Metrics: {stream?.name}
        </DialogTitle>
        <DialogDescription className="flex items-center gap-2">
          Detailed metrics and replicated tables for this CDC stream
          {stream?.cdc_method && <MethodBadge method={stream.cdc_method} />}
        </DialogDescription>
      </DialogHeader>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-6">
            {/* Stream Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground">Status</p>
                <p className="text-lg font-semibold capitalize">{metrics?.status || stream?.status || 'Unknown'}</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground">Tables</p>
                <p className="text-lg font-semibold">{tables?.total_tables || 0}</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground">Total Rows</p>
                <p className="text-lg font-semibold">{(tables?.total_rows || stream?.total_rows_synced || 0).toLocaleString()}</p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground">Total Size</p>
                <p className="text-lg font-semibold">{formatBytes(tables?.total_bytes)}</p>
              </div>
            </div>

            {/* Polling-specific metrics */}
            {stream?.cdc_method !== 'datastream' && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                  <Timer className="w-4 h-4" />
                  Polling Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Poll Interval:</span>
                    <span className="ml-2 font-medium">{stream?.poll_interval_minutes} minutes</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Timestamp Column:</span>
                    <span className="ml-2 font-mono text-xs">{stream?.timestamp_column || 'Auto-detect'}</span>
                  </div>
                  {stream?.last_poll_at && (
                    <div>
                      <span className="text-muted-foreground">Last Poll:</span>
                      <span className="ml-2">{new Date(stream.last_poll_at).toLocaleString()}</span>
                    </div>
                  )}
                  {stream?.last_poll_status && (
                    <div>
                      <span className="text-muted-foreground">Last Status:</span>
                      <span className="ml-2">{stream.last_poll_status}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Errors if any */}
            {metrics?.errors && metrics.errors.length > 0 && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="font-semibold text-red-700 mb-2">Errors</h4>
                {metrics.errors.map((err, i) => (
                  <p key={i} className="text-sm text-red-600">{err.message}</p>
                ))}
              </div>
            )}

            {/* Replicated Tables */}
            <div>
              <h4 className="font-semibold mb-3">Replicated Tables</h4>
              {tables?.tables && tables.tables.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-[300px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-3 font-medium">Table</th>
                          <th className="text-right p-3 font-medium">Rows</th>
                          <th className="text-right p-3 font-medium">Size</th>
                          <th className="text-right p-3 font-medium">Modified</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tables.tables.map((table) => (
                          <tr key={table.table_id} className="border-t">
                            <td className="p-3 font-mono text-xs">{table.table_id}</td>
                            <td className="p-3 text-right">{(table.num_rows || 0).toLocaleString()}</td>
                            <td className="p-3 text-right">{formatBytes(table.num_bytes)}</td>
                            <td className="p-3 text-right text-muted-foreground">
                              {table.modified ? new Date(table.modified).toLocaleDateString() : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No tables replicated yet</p>
              )}
            </div>

            {/* Stream Objects (from Datastream) */}
            {metrics?.objects && metrics.objects.length > 0 && (
              <div>
                <h4 className="font-semibold mb-3">Source Objects</h4>
                <div className="space-y-2">
                  {metrics.objects.map((obj, i) => (
                    <div key={i} className="p-3 bg-muted rounded-lg flex items-center justify-between">
                      <div>
                        <p className="font-mono text-sm">{obj.source_object?.schema}.{obj.source_object?.table}</p>
                        <p className="text-xs text-muted-foreground">Backfill: {obj.backfill_status}</p>
                      </div>
                      {obj.errors && obj.errors.length > 0 && (
                        <Badge variant="destructive">Error</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      )}
    </DialogContent>
  </Dialog>
);

export default function CDC() {
  const [streams, setStreams] = useState([]);
  const [oracleSources, setOracleSources] = useState([]);
  const [oracleConnections, setOracleConnections] = useState([]);
  const [sqlserverConnections, setSqlserverConnections] = useState([]);
  const [postgresqlConnections, setPostgresqlConnections] = useState([]);
  const [bigqueryConnections, setBigqueryConnections] = useState([]);
  const [transformations, setTransformations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // CDC Methods documentation - now source-specific
  const [cdcMethods, setCdcMethods] = useState(null);
  const [mergeStrategies, setMergeStrategies] = useState(null);
  const [selectedSourceType, setSelectedSourceType] = useState("oracle"); // For stream creation

  // Metrics dialog state
  const [metricsDialogOpen, setMetricsDialogOpen] = useState(false);
  const [selectedStream, setSelectedStream] = useState(null);
  const [streamMetrics, setStreamMetrics] = useState(null);
  const [streamTables, setStreamTables] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // CDC Source form - supports Oracle, SQL Server, PostgreSQL
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [sourceType, setSourceType] = useState("oracle"); // oracle, sqlserver, postgresql
  const [sourceForm, setSourceForm] = useState({
    name: "",
    source_type: "oracle",
    connection_id: "",
    selectedSchemas: [],
    selectedTables: [],
  });
  const [availableSchemas, setAvailableSchemas] = useState([]);
  const [availableTables, setAvailableTables] = useState([]);
  const [schemasLoading, setSchemasLoading] = useState(false);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [tableSearchFilter, setTableSearchFilter] = useState("");

  // All CDC sources (generic - supports all source types)
  const [cdcSources, setCdcSources] = useState([]);

  // Stream form with method selection - now 3 steps
  const [streamDialogOpen, setStreamDialogOpen] = useState(false);
  const [streamDialogStep, setStreamDialogStep] = useState(1); // 1: method, 2: destination/merge, 3: configuration
  const [showMethodDetails, setShowMethodDetails] = useState(false);
  const [streamForm, setStreamForm] = useState({
    name: "",
    cdc_method: "",
    oracle_source_id: "",
    source_id: "",
    bigquery_connection_id: "",
    destination_dataset: "cdc_bronze",
    destination_mode: "new_tables",
    merge_strategy: "upsert",
    primary_key_source: "auto_detect",
    primary_key_columns: {},
    poll_interval_minutes: 5,
    timestamp_column: "LAST_UPDATE_DATE",
    batch_size: 10000,
    // PostgreSQL Datastream options
    publication: "datastream_publication",
    replication_slot: "datastream_slot",
    skip_backfill: true,  // Default to skip backfill (CDC only)
    // Multi-tenant options
    tenant_id: "",
    tenant_column_name: "tenant_id",
    auto_route_by_tenant: false,
    apply_rls_on_gold: false,
  });
  const [detectedPrimaryKeys, setDetectedPrimaryKeys] = useState(null);
  const [pkLoading, setPkLoading] = useState(false);
  const [tenants, setTenants] = useState([]);

  // Transformation form
  const [transformDialogOpen, setTransformDialogOpen] = useState(false);
  const [cdcTables, setCdcTables] = useState([]);
  const [selectedTableSchema, setSelectedTableSchema] = useState(null);
  const [transformForm, setTransformForm] = useState({
    name: "",
    cdc_stream_id: "",
    source_table: "",
    transformation_type: "silver",
    target_dataset: "cdc_silver",
    target_table: "",
    fields: [],
    filter_condition: "",
    // Multi-tenant options
    tenant_aware: true,
    tenant_merge_strategy: "merge_by_tenant",
    auto_apply_rls: false,
  });

  // Run Transformation for Tenant dialog state
  const [runTransformDialogOpen, setRunTransformDialogOpen] = useState(false);
  const [selectedTransformation, setSelectedTransformation] = useState(null);
  const [selectedTenantIdForRun, setSelectedTenantIdForRun] = useState("");
  const [selectedUnitIdForRun, setSelectedUnitIdForRun] = useState("");
  const [availableUnitsForRun, setAvailableUnitsForRun] = useState([]);

  useEffect(() => {
    fetchData();
    fetchCdcMethods();
    fetchMergeStrategies();
  }, []);

  const fetchData = async () => {
    try {
      const [streamsData, oracleSourcesData, genericSourcesData, oracleData, sqlserverData, postgresqlData, bqData, transData, tenantsData] = await Promise.all([
        api.getCdcStreams(),
        api.getCdcOracleSources(),
        api.getCdcSources ? api.getCdcSources() : Promise.resolve([]),
        api.getOracleConnections(),
        api.getSQLServerConnections(),
        api.getPostgreSQLConnections(),
        api.getBigQueryConnections(),
        api.getCdcTransformations(),
        api.getTenants(),
      ]);
      setStreams(streamsData);
      setOracleSources(oracleSourcesData);
      setCdcSources(genericSourcesData);
      setOracleConnections(oracleData);
      setSqlserverConnections(sqlserverData);
      setPostgresqlConnections(postgresqlData);
      setBigqueryConnections(bqData);
      setTransformations(transData);
      setTenants(tenantsData);
    } catch (error) {
      toast.error("Failed to load CDC data");
    } finally {
      setLoading(false);
    }
  };

  const fetchCdcMethods = async (sourceType = 'oracle') => {
    try {
      // Try source-specific endpoint first
      if (api.getCdcMethodsBySource) {
        const data = await api.getCdcMethodsBySource(sourceType);
        setCdcMethods(data);
      } else {
        // Fallback to legacy endpoint
        const data = await api.getCdcMethods();
        setCdcMethods(data);
      }
    } catch (error) {
      console.error("Failed to load CDC methods:", error);
      // Fallback to legacy endpoint
      try {
        const data = await api.getCdcMethods();
        setCdcMethods(data);
      } catch (e) {
        console.error("Failed to load CDC methods (fallback):", e);
      }
    }
  };

  const fetchMergeStrategies = async () => {
    try {
      const data = await api.getCdcMergeStrategies();
      setMergeStrategies(data);
    } catch (error) {
      console.error("Failed to load merge strategies:", error);
    }
  };

  // Auto-detect primary keys for selected Oracle source
  const handleDetectPrimaryKeys = async (sourceId) => {
    if (!sourceId) return;
    setPkLoading(true);
    setDetectedPrimaryKeys(null);
    try {
      const data = await api.detectCdcPrimaryKeys(sourceId);
      setDetectedPrimaryKeys(data);
      // Auto-populate primary key columns
      setStreamForm(prev => ({
        ...prev,
        primary_key_columns: data.primary_keys || {}
      }));
      if (data.tables_without_pk?.length > 0) {
        toast.warning(`${data.tables_without_pk.length} table(s) have no primary key`);
      } else {
        toast.success(`Detected primary keys for ${data.summary.total_tables_with_pk} table(s)`);
      }
    } catch (error) {
      toast.error("Failed to detect primary keys");
    } finally {
      setPkLoading(false);
    }
  };

  // Handle source type change
  const handleSourceTypeChange = (newSourceType) => {
    setSourceType(newSourceType);
    setSourceForm({
      name: sourceForm.name, // Keep name
      source_type: newSourceType,
      connection_id: "",
      selectedSchemas: [],
      selectedTables: [],
    });
    setAvailableSchemas([]);
    setAvailableTables([]);
  };

  // Handle connection selection - load schemas (supports all source types)
  const handleConnectionSelect = async (connectionId) => {
    setSourceForm({ 
      ...sourceForm, 
      connection_id: connectionId,
      selectedSchemas: [],
      selectedTables: []
    });
    setAvailableSchemas([]);
    setAvailableTables([]);
    
    if (!connectionId) return;
    
    setSchemasLoading(true);
    try {
      let data;
      if (sourceType === 'oracle') {
        data = await api.getOracleSchemas(connectionId);
      } else if (sourceType === 'sqlserver') {
        data = await api.getSQLServerSchemas(connectionId);
      } else if (sourceType === 'postgresql') {
        data = await api.getPostgreSQLSchemas(connectionId);
      }
      setAvailableSchemas(data?.schemas || []);
    } catch (error) {
      toast.error("Failed to load schemas");
    } finally {
      setSchemasLoading(false);
    }
  };

  // Handle Oracle connection selection - load schemas (legacy - for backward compatibility)
  const handleOracleConnectionSelect = async (connectionId) => {
    setSourceForm({ 
      ...sourceForm, 
      oracle_connection_id: connectionId,
      connection_id: connectionId,
      selectedSchemas: [],
      selectedTables: []
    });
    setAvailableSchemas([]);
    setAvailableTables([]);
    
    if (!connectionId) return;
    
    setSchemasLoading(true);
    try {
      const data = await api.getOracleSchemas(connectionId);
      setAvailableSchemas(data.schemas || []);
    } catch (error) {
      toast.error("Failed to load schemas");
    } finally {
      setSchemasLoading(false);
    }
  };

  // Handle schema selection - load tables (supports all source types)
  const handleSchemaToggle = async (schemaName, checked) => {
    let newSelectedSchemas;
    if (checked) {
      newSelectedSchemas = [...sourceForm.selectedSchemas, schemaName];
    } else {
      newSelectedSchemas = sourceForm.selectedSchemas.filter(s => s !== schemaName);
      // Also remove tables from this schema
      const newSelectedTables = sourceForm.selectedTables.filter(t => !t.startsWith(schemaName + "."));
      setSourceForm({ ...sourceForm, selectedSchemas: newSelectedSchemas, selectedTables: newSelectedTables });
      return;
    }
    
    setSourceForm({ ...sourceForm, selectedSchemas: newSelectedSchemas });
    
    // Load tables for newly selected schema
    if (checked) {
      setTablesLoading(true);
      try {
        let data;
        const connectionId = sourceForm.connection_id || sourceForm.oracle_connection_id;
        
        if (sourceType === 'oracle') {
          data = await api.getOracleTables(connectionId, schemaName);
          const newTables = (data.tables || []).map(t => ({
            ...t,
            fullName: `${t.owner || schemaName}.${t.table_name}`
          }));
          setAvailableTables(prev => {
            const existing = prev.filter(t => (t.owner || t.schema) !== schemaName);
            return [...existing, ...newTables];
          });
        } else if (sourceType === 'sqlserver') {
          data = await api.getSQLServerTables(connectionId, schemaName);
          const newTables = (data.tables || []).map(t => ({
            ...t,
            owner: schemaName,
            fullName: `${schemaName}.${t.table_name}`
          }));
          setAvailableTables(prev => {
            const existing = prev.filter(t => t.owner !== schemaName);
            return [...existing, ...newTables];
          });
        } else if (sourceType === 'postgresql') {
          data = await api.getPostgreSQLTables(connectionId, schemaName);
          const newTables = (data.tables || []).map(t => ({
            ...t,
            owner: schemaName,
            fullName: `${schemaName}.${t.table_name}`
          }));
          setAvailableTables(prev => {
            const existing = prev.filter(t => t.owner !== schemaName);
            return [...existing, ...newTables];
          });
        }
      } catch (error) {
        toast.error(`Failed to load tables for ${schemaName}`);
      } finally {
        setTablesLoading(false);
      }
    }
  };

  // Handle table selection toggle
  const handleTableToggle = (tableFullName, checked) => {
    if (checked) {
      setSourceForm({ ...sourceForm, selectedTables: [...sourceForm.selectedTables, tableFullName] });
    } else {
      setSourceForm({ ...sourceForm, selectedTables: sourceForm.selectedTables.filter(t => t !== tableFullName) });
    }
  };

  // Select all tables for a schema
  const handleSelectAllTables = (schemaName) => {
    const schemaTables = availableTables
      .filter(t => t.owner === schemaName)
      .map(t => t.fullName);
    const otherSelectedTables = sourceForm.selectedTables.filter(t => !t.startsWith(schemaName + "."));
    setSourceForm({ ...sourceForm, selectedTables: [...otherSelectedTables, ...schemaTables] });
  };

  // Deselect all tables for a schema
  const handleDeselectAllTables = (schemaName) => {
    const newSelectedTables = sourceForm.selectedTables.filter(t => !t.startsWith(schemaName + "."));
    setSourceForm({ ...sourceForm, selectedTables: newSelectedTables });
  };

  const handleCreateSource = async (e) => {
    e.preventDefault();
    try {
      if (sourceForm.selectedSchemas.length === 0) {
        toast.error("Please select at least one schema");
        return;
      }

      const connectionId = sourceForm.connection_id || sourceForm.oracle_connection_id;
      
      // Use new generic API for SQL Server and PostgreSQL, legacy API for Oracle
      if (sourceType === 'oracle') {
        await api.createCdcOracleSource({
          name: sourceForm.name,
          oracle_connection_id: connectionId,
          schemas: sourceForm.selectedSchemas,
          tables: sourceForm.selectedTables.length > 0 ? sourceForm.selectedTables : null,
        });
        toast.success("Oracle CDC source created");
      } else {
        await api.createCdcSource({
          name: sourceForm.name,
          source_type: sourceType,
          connection_id: connectionId,
          schemas: sourceForm.selectedSchemas,
          tables: sourceForm.selectedTables.length > 0 ? sourceForm.selectedTables : null,
        });
        toast.success(`${sourceType === 'sqlserver' ? 'SQL Server' : 'PostgreSQL'} CDC source created`);
      }
      
      setSourceDialogOpen(false);
      setSourceForm({ name: "", source_type: "oracle", connection_id: "", selectedSchemas: [], selectedTables: [] });
      setAvailableSchemas([]);
      setAvailableTables([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create source");
    }
  };

  const handleSourceDialogClose = (open) => {
    setSourceDialogOpen(open);
    if (!open) {
      setSourceForm({ name: "", source_type: "oracle", connection_id: "", selectedSchemas: [], selectedTables: [] });
      setSourceType("oracle");
      setAvailableSchemas([]);
      setAvailableTables([]);
      setTableSearchFilter("");
    }
  };

  const handleStreamDialogClose = (open) => {
    setStreamDialogOpen(open);
    if (!open) {
      setStreamDialogStep(1);
      setShowMethodDetails(false);
      setDetectedPrimaryKeys(null);
      setSelectedSourceType("oracle");
      setStreamForm({
        name: "",
        cdc_method: "",
        source_type: "oracle",
        source_id: "",
        oracle_source_id: "",
        bigquery_connection_id: "",
        destination_dataset: "cdc_bronze",
        destination_mode: "new_tables",
        merge_strategy: "upsert",
        primary_key_source: "auto_detect",
        primary_key_columns: {},
        poll_interval_minutes: 5,
        timestamp_column: "LAST_UPDATE_DATE",
        batch_size: 10000,
        // Multi-tenant options
        tenant_id: "",
        tenant_column_name: "tenant_id",
        auto_route_by_tenant: false,
        apply_rls_on_gold: false,
      });
      // Reset CDC methods to Oracle
      fetchCdcMethods('oracle');
    }
  };

  // Handle source type change for stream creation
  const handleStreamSourceTypeChange = (newSourceType) => {
    setSelectedSourceType(newSourceType);
    setStreamForm(prev => ({
      ...prev,
      source_type: newSourceType,
      source_id: "",
      oracle_source_id: "",
      cdc_method: "", // Reset method when source type changes
    }));
    // Fetch CDC methods for the new source type
    fetchCdcMethods(newSourceType);
  };

  // Get available sources for the selected source type
  const getAvailableSources = () => {
    if (selectedSourceType === 'oracle') {
      return oracleSources;
    }
    return cdcSources.filter(s => s.source_type === selectedSourceType);
  };

  const handleCreateStream = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: streamForm.name,
        cdc_method: streamForm.cdc_method,
        // Source-agnostic fields
        source_type: selectedSourceType,
        source_id: streamForm.source_id || streamForm.oracle_source_id,
        oracle_source_id: selectedSourceType === 'oracle' ? (streamForm.oracle_source_id || streamForm.source_id) : null,
        bigquery_connection_id: streamForm.bigquery_connection_id,
        destination_dataset: streamForm.destination_dataset,
        destination_mode: streamForm.destination_mode,
        merge_strategy: streamForm.merge_strategy,
        primary_key_source: streamForm.primary_key_source,
        primary_key_columns: streamForm.primary_key_source !== 'none' ? streamForm.primary_key_columns : null,
        // Multi-tenant options
        tenant_id: streamForm.tenant_id || null,
        tenant_column_name: streamForm.tenant_column_name,
        auto_route_by_tenant: streamForm.auto_route_by_tenant,
        apply_rls_on_gold: streamForm.apply_rls_on_gold,
      };
      
      // Add polling-specific fields
      if (streamForm.cdc_method !== 'datastream') {
        payload.poll_interval_minutes = streamForm.poll_interval_minutes;
        payload.timestamp_column = streamForm.timestamp_column;
        payload.batch_size = streamForm.batch_size;
      }
      
      // Add PostgreSQL Datastream specific fields
      if (selectedSourceType === 'postgresql' && streamForm.cdc_method === 'datastream') {
        payload.publication = streamForm.publication;
        payload.replication_slot = streamForm.replication_slot;
        payload.skip_backfill = streamForm.skip_backfill;
      }
      
      await api.createCdcStream(payload);
      
      const methodName = cdcMethods?.methods?.find(m => m.id === streamForm.cdc_method)?.name || streamForm.cdc_method;
      toast.success(`CDC Stream created using ${methodName}`);
      handleStreamDialogClose(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create stream");
    }
  };

  const handleStartStream = async (id) => {
    setActionLoading(id);
    try {
      await api.startCdcStream(id);
      toast.success("CDC Stream started");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start stream");
    } finally {
      setActionLoading(null);
    }
  };

  const handlePauseStream = async (id) => {
    setActionLoading(id);
    try {
      await api.pauseCdcStream(id);
      toast.success("CDC Stream paused");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to pause stream");
    } finally {
      setActionLoading(null);
    }
  };

  const handlePollNow = async (id) => {
    setActionLoading(id);
    try {
      await api.pollCdcNow(id);
      toast.success("Polling triggered");
      setTimeout(fetchData, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to trigger polling");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRefreshStream = async (id) => {
    setActionLoading(id);
    try {
      await api.getCdcStream(id);
      fetchData();
    } catch (error) {
      toast.error("Failed to refresh stream status");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteStream = async (id) => {
    try {
      await api.deleteCdcStream(id);
      toast.success("CDC Stream deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete stream");
    }
  };

  const handleDeleteSource = async (id) => {
    try {
      await api.deleteCdcOracleSource(id);
      toast.success("Oracle source deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete source");
    }
  };

  // Delete generic CDC source (SQL Server, PostgreSQL)
  const handleDeleteGenericSource = async (id) => {
    try {
      await api.deleteCdcSource(id);
      toast.success("CDC source deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete source");
    }
  };

  const handleViewMetrics = async (stream) => {
    setSelectedStream(stream);
    setMetricsDialogOpen(true);
    setMetricsLoading(true);
    
    try {
      const [metrics, tables] = await Promise.all([
        api.getCdcStreamMetrics(stream.id),
        api.getCdcStreamTables(stream.id),
      ]);
      setStreamMetrics(metrics);
      setStreamTables(tables);
    } catch (error) {
      toast.error("Failed to load metrics");
    } finally {
      setMetricsLoading(false);
    }
  };

  // Transformation handlers
  const handleStreamSelectForTransform = async (streamId) => {
    setTransformForm({ ...transformForm, cdc_stream_id: streamId, source_table: "" });
    try {
      const data = await api.getCdcDatasetTables(streamId);
      setCdcTables(data.tables || []);
    } catch (error) {
      toast.error("Failed to load CDC tables");
      setCdcTables([]);
    }
  };

  const handleSourceTableSelect = async (tableFullId) => {
    const [dataset, table] = tableFullId.split(".");
    setTransformForm({ 
      ...transformForm, 
      source_table: table,
      target_table: transformForm.transformation_type === 'silver' ? `clean_${table}` : `gold_${table}`
    });
    
    // Load schema
    const stream = streams.find(s => s.id === transformForm.cdc_stream_id);
    if (stream) {
      try {
        const schema = await api.getCdcTableSchema(stream.bigquery_connection_id, dataset, table);
        setSelectedTableSchema(schema);
        // Auto-populate fields
        const fields = schema.schema
          .filter(f => !f.name.startsWith('_metadata'))
          .map(f => ({
            source_field: f.name,
            target_field: f.name,
            data_type: f.type,
          }));
        setTransformForm(prev => ({ ...prev, fields }));
      } catch (error) {
        toast.error("Failed to load table schema");
      }
    }
  };

  const handleCreateTransformation = async (e) => {
    e.preventDefault();
    try {
      await api.createCdcTransformation({
        name: transformForm.name,
        cdc_stream_id: transformForm.cdc_stream_id,
        source_table: transformForm.source_table,
        transformation_type: transformForm.transformation_type,
        target_dataset: transformForm.target_dataset,
        target_table: transformForm.target_table,
        fields: transformForm.fields,
        filter_condition: transformForm.filter_condition || null,
        // Multi-tenant options
        tenant_aware: transformForm.tenant_aware,
        tenant_merge_strategy: transformForm.tenant_merge_strategy,
        auto_apply_rls: transformForm.auto_apply_rls,
      });
      toast.success("Transformation created");
      setTransformDialogOpen(false);
      setTransformForm({
        name: "",
        cdc_stream_id: "",
        source_table: "",
        transformation_type: "silver",
        target_dataset: "cdc_silver",
        target_table: "",
        fields: [],
        filter_condition: "",
        // Multi-tenant options
        tenant_aware: true,
        tenant_merge_strategy: "merge_by_tenant",
        auto_apply_rls: false,
      });
      setCdcTables([]);
      setSelectedTableSchema(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create transformation");
    }
  };

  // Open Run for Tenant dialog
  const handleRunTransformationClick = (transformation) => {
    setSelectedTransformation(transformation);
    setSelectedTenantIdForRun("");
    setSelectedUnitIdForRun("");
    setAvailableUnitsForRun([]);
    setRunTransformDialogOpen(true);
  };

  // Handle tenant selection and load units
  const handleTenantSelectForRun = (tenantId) => {
    setSelectedTenantIdForRun(tenantId);
    setSelectedUnitIdForRun("");
    
    // Find the selected tenant and get its units
    const tenant = tenants.find(t => t.tenant_id === tenantId);
    if (tenant && tenant.units && tenant.units.length > 0) {
      setAvailableUnitsForRun(tenant.units);
    } else {
      setAvailableUnitsForRun([]);
    }
  };

  // Execute transformation for selected tenant and unit
  const executeTransformationForTenant = async () => {
    if (!selectedTenantIdForRun) {
      toast.error("Please select a tenant");
      return;
    }

    // If tenant has units, require unit selection
    if (availableUnitsForRun.length > 0 && !selectedUnitIdForRun) {
      toast.error("Please select an accounting unit");
      return;
    }

    setRunTransformDialogOpen(false);
    setActionLoading(selectedTransformation.id);
    
    try {
      await api.runCdcTransformation(
        selectedTransformation.id, 
        selectedTenantIdForRun,
        selectedUnitIdForRun || null
      );
      
      let msg = `Transformation started for tenant: ${selectedTenantIdForRun}`;
      if (selectedUnitIdForRun) {
        msg += `, unit: ${selectedUnitIdForRun}`;
      }
      toast.success(msg);
      setTimeout(fetchData, 2000); // Refresh after delay
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to run transformation");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteTransformation = async (id) => {
    try {
      await api.deleteCdcTransformation(id);
      toast.success("Transformation deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete transformation");
    }
  };

  const selectedMethodInfo = cdcMethods?.methods?.find(m => m.id === streamForm.cdc_method);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cdc-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">CDC - Change Data Capture</h1>
          <p className="text-muted-foreground">
            Real-time and near real-time data replication from Oracle, SQL Server, and PostgreSQL to BigQuery
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="bg-gradient-to-r from-purple-50 via-blue-50 to-teal-50 border-purple-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Activity className="w-6 h-6 text-purple-500" />
            </div>
            <div>
              <h3 className="font-semibold">Multiple CDC Methods & Source Types</h3>
              <p className="text-sm text-muted-foreground mt-1">
                <strong>Oracle:</strong> Datastream (real-time), Timestamp Polling, MView Logs | 
                <strong> SQL Server:</strong> Timestamp Polling, Change Tracking, Native CDC | 
                <strong> PostgreSQL:</strong> Timestamp Polling, Logical Replication
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="streams" className="space-y-6">
        <TabsList>
          <TabsTrigger value="streams" className="gap-2">
            <Activity className="w-4 h-4" />
            CDC Streams ({streams.length})
          </TabsTrigger>
          <TabsTrigger value="transformations" className="gap-2">
            <Layers className="w-4 h-4" />
            Transformations ({transformations.length})
          </TabsTrigger>
          <TabsTrigger value="sources" className="gap-2">
            <Database className="w-4 h-4" />
            CDC Sources ({oracleSources.length + cdcSources.length})
          </TabsTrigger>
        </TabsList>

        {/* Streams Tab */}
        <TabsContent value="streams" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">CDC Streams</h2>
              <p className="text-sm text-muted-foreground">Active data replication streams</p>
            </div>
            <Dialog open={streamDialogOpen} onOpenChange={handleStreamDialogClose}>
              <DialogTrigger asChild>
                <Button data-testid="create-stream-btn" disabled={(oracleSources.length + cdcSources.length) === 0}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Stream
                </Button>
              </DialogTrigger>
              <DialogContent className={streamDialogStep === 1 ? "max-w-4xl" : streamDialogStep === 2 ? "max-w-3xl" : "max-w-lg"}>
                <DialogHeader>
                  <DialogTitle>
                    {streamDialogStep === 1 && "Step 1: Choose Source Type & CDC Method"}
                    {streamDialogStep === 2 && "Step 2: Destination & Merge Strategy"}
                    {streamDialogStep === 3 && "Step 3: Configure CDC Stream"}
                  </DialogTitle>
                  <DialogDescription>
                    {streamDialogStep === 1 && "Select your source database type and the best CDC method for your environment"}
                    {streamDialogStep === 2 && "Configure how data is written to BigQuery"}
                    {streamDialogStep === 3 && `Configure your ${selectedMethodInfo?.name || ''} CDC stream`}
                  </DialogDescription>
                </DialogHeader>
                
                {/* Step indicator */}
                <div className="flex items-center justify-center gap-2 py-2">
                  {[1, 2, 3].map((step) => (
                    <div key={step} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        streamDialogStep >= step 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {step}
                      </div>
                      {step < 3 && (
                        <div className={`w-12 h-0.5 ${streamDialogStep > step ? 'bg-primary' : 'bg-muted'}`} />
                      )}
                    </div>
                  ))}
                </div>
                
                {streamDialogStep === 1 ? (
                  <div className="space-y-4">
                    {/* Source Type Selection */}
                    <div className="space-y-2">
                      <Label className="text-base font-semibold">Source Database Type</Label>
                      <div className="flex gap-2">
                        {[
                          { id: 'oracle', label: 'Oracle', color: 'orange', icon: '🔶' },
                          { id: 'sqlserver', label: 'SQL Server', color: 'blue', icon: '🔷' },
                          { id: 'postgresql', label: 'PostgreSQL', color: 'teal', icon: '🐘' }
                        ].map((type) => (
                          <Button
                            key={type.id}
                            variant={selectedSourceType === type.id ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleStreamSourceTypeChange(type.id)}
                            className={selectedSourceType === type.id ? "" : "hover:bg-muted"}
                            data-testid={`stream-source-type-${type.id}`}
                          >
                            <span className="mr-1">{type.icon}</span>
                            {type.label}
                          </Button>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {selectedSourceType === 'oracle' && "Oracle supports: Datastream (real-time), Timestamp Polling, MView Logs"}
                        {selectedSourceType === 'sqlserver' && "SQL Server supports: Timestamp Polling, Change Tracking, Native CDC"}
                        {selectedSourceType === 'postgresql' && "PostgreSQL supports: Datastream (real-time), Timestamp Polling, Logical Replication"}
                      </p>
                    </div>
                    
                    {/* Method Selection Cards */}
                    <div className="space-y-2">
                      <Label className="text-base font-semibold">CDC Method</Label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {cdcMethods?.methods?.map((method) => (
                          <CDCMethodCard
                            key={method.id}
                            method={method}
                            selected={streamForm.cdc_method === method.id}
                            onSelect={(id) => setStreamForm({ ...streamForm, cdc_method: id })}
                          />
                        ))}
                      </div>
                    </div>
                    
                    {/* Detailed comparison toggle */}
                    {streamForm.cdc_method && (
                      <MethodDetailsPanel
                        method={selectedMethodInfo}
                        isOpen={showMethodDetails}
                        onToggle={setShowMethodDetails}
                      />
                    )}
                    
                    <DialogFooter>
                      <Button
                        onClick={() => setStreamDialogStep(2)}
                        disabled={!streamForm.cdc_method}
                        data-testid="continue-to-destination"
                      >
                        Continue
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </DialogFooter>
                  </div>
                ) : streamDialogStep === 2 ? (
                  <>
                    <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
                      {/* Destination Mode Selection */}
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Destination Mode</Label>
                      <div className="grid grid-cols-2 gap-3">
                        {mergeStrategies?.destination_modes?.map((mode) => (
                          <Card 
                            key={mode.id}
                            className={`cursor-pointer p-4 transition-all ${
                              streamForm.destination_mode === mode.id 
                                ? 'ring-2 ring-primary' 
                                : 'hover:border-primary/50'
                            }`}
                            onClick={() => setStreamForm({ ...streamForm, destination_mode: mode.id })}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                streamForm.destination_mode === mode.id ? 'border-primary bg-primary' : 'border-muted-foreground'
                              }`}>
                                {streamForm.destination_mode === mode.id && (
                                  <Check className="w-2.5 h-2.5 text-white" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{mode.name}</p>
                                <p className="text-xs text-muted-foreground mt-1">{mode.description}</p>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </div>

                    {/* Merge Strategy Selection */}
                    <div className="space-y-3">
                      <Label className="text-base font-semibold">Merge Strategy</Label>
                      <p className="text-xs text-muted-foreground">How should CDC handle data changes?</p>
                      <div className="space-y-2">
                        {mergeStrategies?.strategies?.map((strategy) => {
                          const isDisabled = strategy.id === 'full_merge' && streamForm.cdc_method === 'timestamp_polling';
                          return (
                            <Card 
                              key={strategy.id}
                              className={`cursor-pointer p-4 transition-all ${
                                isDisabled ? 'opacity-50 cursor-not-allowed' :
                                streamForm.merge_strategy === strategy.id 
                                  ? 'ring-2 ring-primary' 
                                  : 'hover:border-primary/50'
                              }`}
                              onClick={() => !isDisabled && setStreamForm({ ...streamForm, merge_strategy: strategy.id })}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                  streamForm.merge_strategy === strategy.id ? 'border-primary bg-primary' : 'border-muted-foreground'
                                }`}>
                                  {streamForm.merge_strategy === strategy.id && (
                                    <Check className="w-2.5 h-2.5 text-white" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-sm">{strategy.name}</p>
                                    <Badge variant="outline" className="text-[10px]">{strategy.bigquery_operation}</Badge>
                                    {strategy.requires_pk && (
                                      <Badge variant="secondary" className="text-[10px]">Requires PK</Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-1">{strategy.description}</p>
                                  {isDisabled && (
                                    <p className="text-xs text-red-500 mt-1">Not available with Timestamp Polling (cannot capture DELETEs)</p>
                                  )}
                                </div>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    </div>

                    {/* Primary Key Configuration */}
                    {streamForm.merge_strategy !== 'append' && (
                      <div className="space-y-3">
                        <Label className="text-base font-semibold">Primary Key Configuration</Label>
                        <p className="text-xs text-muted-foreground">Primary keys identify unique records for upsert operations</p>
                        <div className="space-y-2">
                          {mergeStrategies?.primary_key_options?.map((option) => (
                            <Card 
                              key={option.id}
                              className={`cursor-pointer p-3 transition-all ${
                                streamForm.primary_key_source === option.id 
                                  ? 'ring-2 ring-primary' 
                                  : 'hover:border-primary/50'
                              }`}
                              onClick={() => setStreamForm({ ...streamForm, primary_key_source: option.id })}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                  streamForm.primary_key_source === option.id ? 'border-primary bg-primary' : 'border-muted-foreground'
                                }`}>
                                  {streamForm.primary_key_source === option.id && (
                                    <Check className="w-2.5 h-2.5 text-white" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-sm">{option.name}</p>
                                    {option.recommended && (
                                      <Badge className="text-[10px] bg-green-100 text-green-700">Recommended</Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-1">{option.description}</p>
                                  {option.warning && (
                                    <p className="text-xs text-yellow-600 mt-1">⚠️ {option.warning}</p>
                                  )}
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <DialogFooter className="gap-2 pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => setStreamDialogStep(1)}>
                      Back
                    </Button>
                    <Button
                      onClick={() => setStreamDialogStep(3)}
                      data-testid="continue-to-config"
                    >
                      Continue
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </DialogFooter>
                  </>
                ) : (
                  <form onSubmit={handleCreateStream} className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                    {/* Selected Configuration Summary */}
                    <div className="p-3 bg-muted rounded-lg space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-muted-foreground">Method:</span>
                        <MethodBadge method={streamForm.cdc_method} />
                        <span className="text-muted-foreground">•</span>
                        <Badge variant="outline" className="text-xs">
                          {streamForm.destination_mode === 'new_tables' ? 'New Tables' : 'Existing Tables'}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {mergeStrategies?.strategies?.find(s => s.id === streamForm.merge_strategy)?.name || streamForm.merge_strategy}
                        </Badge>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs p-0 h-auto"
                        onClick={() => setStreamDialogStep(1)}
                      >
                        Change settings
                      </Button>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Stream Name</Label>
                      <Input
                        value={streamForm.name}
                        onChange={(e) => setStreamForm({ ...streamForm, name: e.target.value })}
                        placeholder="e.g., erp_sales_cdc"
                        required
                        data-testid="stream-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>{selectedSourceType === 'oracle' ? 'Oracle' : selectedSourceType === 'sqlserver' ? 'SQL Server' : 'PostgreSQL'} Source</Label>
                      <Select
                        value={streamForm.source_id || streamForm.oracle_source_id}
                        onValueChange={(val) => {
                          setStreamForm({ 
                            ...streamForm, 
                            source_id: val,
                            oracle_source_id: selectedSourceType === 'oracle' ? val : ''
                          });
                          // Auto-detect PKs if auto_detect is selected (Oracle only for now)
                          if (streamForm.primary_key_source === 'auto_detect' && val && selectedSourceType === 'oracle') {
                            handleDetectPrimaryKeys(val);
                          }
                        }}
                        required
                      >
                        <SelectTrigger data-testid="stream-source-select">
                          <SelectValue placeholder={`Select ${selectedSourceType === 'oracle' ? 'Oracle' : selectedSourceType === 'sqlserver' ? 'SQL Server' : 'PostgreSQL'} source`} />
                        </SelectTrigger>
                        <SelectContent>
                          {getAvailableSources().map((source) => (
                            <SelectItem key={source.id} value={source.id}>
                              {source.name} ({source.schemas.join(", ")})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {getAvailableSources().length === 0 && (
                        <p className="text-xs text-amber-600">
                          No {selectedSourceType === 'oracle' ? 'Oracle' : selectedSourceType === 'sqlserver' ? 'SQL Server' : 'PostgreSQL'} CDC sources found. Create one in the Sources tab first.
                        </p>
                      )}
                    </div>

                    {/* Primary Key Detection Results */}
                    {streamForm.primary_key_source === 'auto_detect' && (streamForm.source_id || streamForm.oracle_source_id) && selectedSourceType === 'oracle' && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Primary Keys</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleDetectPrimaryKeys(streamForm.source_id || streamForm.oracle_source_id)}
                            disabled={pkLoading}
                          >
                            {pkLoading ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3 h-3 mr-1" />
                            )}
                            Detect PKs
                          </Button>
                        </div>
                        {detectedPrimaryKeys && (
                          <div className="p-3 bg-muted rounded-lg text-xs space-y-2 max-h-32 overflow-y-auto">
                            {Object.entries(detectedPrimaryKeys.primary_keys || {}).map(([table, cols]) => (
                              <div key={table} className="flex items-center gap-2">
                                <Table className="w-3 h-3 text-green-600" />
                                <span className="font-mono">{table}</span>
                                <span className="text-muted-foreground">→</span>
                                <span className="text-green-600">{cols.join(", ")}</span>
                              </div>
                            ))}
                            {detectedPrimaryKeys.tables_without_pk?.length > 0 && (
                              <div className="pt-2 border-t">
                                <p className="text-yellow-600 font-medium">Tables without PK:</p>
                                {detectedPrimaryKeys.tables_without_pk.map((t) => (
                                  <div key={t} className="flex items-center gap-2 text-yellow-600">
                                    <AlertTriangle className="w-3 h-3" />
                                    <span className="font-mono">{t}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Manual PK Entry */}
                    {streamForm.primary_key_source === 'manual' && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs">
                        <p className="font-medium text-blue-700 mb-1">Manual PK Configuration</p>
                        <p className="text-blue-600">
                          Primary keys will be configured per table after stream creation.
                          You can edit the PK columns in the stream settings.
                        </p>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label>BigQuery Destination</Label>
                      <Select
                        value={streamForm.bigquery_connection_id}
                        onValueChange={(val) => setStreamForm({ ...streamForm, bigquery_connection_id: val })}
                        required
                      >
                        <SelectTrigger data-testid="stream-bq-select">
                          <SelectValue placeholder="Select BigQuery connection" />
                        </SelectTrigger>
                        <SelectContent>
                          {bigqueryConnections.map((conn) => (
                            <SelectItem key={conn.id} value={conn.id}>
                              {conn.name} ({conn.project_id})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Destination Dataset</Label>
                      <Input
                        value={streamForm.destination_dataset}
                        onChange={(e) => setStreamForm({ ...streamForm, destination_dataset: e.target.value })}
                        placeholder="cdc_bronze"
                        required
                        data-testid="stream-dataset-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        CDC data will be written to existing tables in this dataset
                      </p>
                    </div>

                    {/* PostgreSQL Datastream Configuration */}
                    {selectedSourceType === 'postgresql' && streamForm.cdc_method === 'datastream' && (
                      <div className="border rounded-lg p-4 space-y-4 bg-teal-50/50">
                        <h4 className="text-sm font-medium flex items-center gap-2">
                          <Database className="w-4 h-4 text-teal-600" />
                          PostgreSQL Datastream Configuration
                        </h4>
                        
                        <div className="space-y-2">
                          <Label>Publication Name <span className="text-red-500">*</span></Label>
                          <Input
                            value={streamForm.publication || ''}
                            onChange={(e) => setStreamForm({ ...streamForm, publication: e.target.value })}
                            placeholder="datastream_publication"
                            required
                            data-testid="stream-publication-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            PostgreSQL publication name. Create with: <code className="bg-muted px-1 rounded">CREATE PUBLICATION name FOR ALL TABLES;</code>
                          </p>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Replication Slot Name <span className="text-red-500">*</span></Label>
                          <Input
                            value={streamForm.replication_slot || ''}
                            onChange={(e) => setStreamForm({ ...streamForm, replication_slot: e.target.value })}
                            placeholder="datastream_slot"
                            required
                            data-testid="stream-replication-slot-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            Replication slot with pgoutput plugin. Create with: <code className="bg-muted px-1 rounded">SELECT pg_create_logical_replication_slot('name', 'pgoutput');</code>
                          </p>
                        </div>

                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="skip_backfill"
                            checked={streamForm.skip_backfill !== false}
                            onChange={(e) => setStreamForm({ ...streamForm, skip_backfill: e.target.checked })}
                            className="rounded border-gray-300"
                          />
                          <Label htmlFor="skip_backfill" className="text-sm font-normal cursor-pointer">
                            Skip initial backfill (CDC only - recommended if data is already loaded)
                          </Label>
                        </div>
                        
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs">
                          <p className="font-medium text-amber-700 mb-1">⚠️ Prerequisites</p>
                          <ul className="text-amber-600 space-y-1 list-disc pl-4">
                            <li>PostgreSQL must have <code>wal_level = logical</code></li>
                            <li>Publication and replication slot must exist before creating stream</li>
                            <li>Google Cloud Datastream IPs must have network access to PostgreSQL</li>
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* Multi-Tenant Configuration */}
                    <div className="border-t pt-4 mt-4">
                      <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Multi-Tenant Configuration
                      </h4>
                      
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Associate with Tenant (Optional)</Label>
                          <Select
                            value={streamForm.tenant_id || "none"}
                            onValueChange={(val) => setStreamForm({ ...streamForm, tenant_id: val === "none" ? "" : val })}
                          >
                            <SelectTrigger data-testid="stream-tenant-select">
                              <SelectValue placeholder="No tenant association" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No tenant association</SelectItem>
                              {tenants.map((tenant) => (
                                <SelectItem key={tenant.id} value={tenant.tenant_id}>
                                  {tenant.name} ({tenant.tenant_id})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            Associate this CDC stream with a specific tenant for data isolation
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label>Tenant Column Name</Label>
                          <Input
                            value={streamForm.tenant_column_name}
                            onChange={(e) => setStreamForm({ ...streamForm, tenant_column_name: e.target.value })}
                            placeholder="tenant_id"
                            data-testid="stream-tenant-column-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            Column name used to identify tenant data in source tables
                          </p>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <Label className="font-normal">Route by Tenant</Label>
                            <p className="text-xs text-muted-foreground">
                              Route data to tenant-specific datasets
                            </p>
                          </div>
                          <Switch
                            checked={streamForm.auto_route_by_tenant}
                            onCheckedChange={(checked) => setStreamForm({ ...streamForm, auto_route_by_tenant: checked })}
                            data-testid="stream-auto-route-switch"
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <Label className="font-normal">Auto-Apply RLS on Gold</Label>
                            <p className="text-xs text-muted-foreground">
                              Automatically create RLS policies for Gold layer tables
                            </p>
                          </div>
                          <Switch
                            checked={streamForm.apply_rls_on_gold}
                            onCheckedChange={(checked) => setStreamForm({ ...streamForm, apply_rls_on_gold: checked })}
                            data-testid="stream-apply-rls-switch"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Polling-specific options */}
                    {streamForm.cdc_method !== 'datastream' && (
                      <>
                        <div className="border-t pt-4 mt-4">
                          <h4 className="text-sm font-medium mb-3">Polling Configuration</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Poll Interval (minutes)</Label>
                              <Select
                                value={String(streamForm.poll_interval_minutes)}
                                onValueChange={(val) => setStreamForm({ ...streamForm, poll_interval_minutes: Number(val) })}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="1">1 minute</SelectItem>
                                  <SelectItem value="5">5 minutes</SelectItem>
                                  <SelectItem value="10">10 minutes</SelectItem>
                                  <SelectItem value="15">15 minutes</SelectItem>
                                  <SelectItem value="30">30 minutes</SelectItem>
                                  <SelectItem value="60">1 hour</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Batch Size</Label>
                              <Select
                                value={String(streamForm.batch_size)}
                                onValueChange={(val) => setStreamForm({ ...streamForm, batch_size: Number(val) })}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="1000">1,000 rows</SelectItem>
                                  <SelectItem value="5000">5,000 rows</SelectItem>
                                  <SelectItem value="10000">10,000 rows</SelectItem>
                                  <SelectItem value="50000">50,000 rows</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                        
                        {streamForm.cdc_method === 'timestamp_polling' && (
                          <div className="space-y-2">
                            <Label>Timestamp Column</Label>
                            <Input
                              value={streamForm.timestamp_column}
                              onChange={(e) => setStreamForm({ ...streamForm, timestamp_column: e.target.value })}
                              placeholder="LAST_UPDATE_DATE"
                            />
                            <p className="text-xs text-muted-foreground">
                              Common columns: LAST_UPDATE_DATE, MODIFIED_DATE, UPD_DATE, CREATED_DATE
                            </p>
                          </div>
                        )}
                        
                        {streamForm.cdc_method === 'mview_log' && (
                          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-xs">
                            <p className="font-medium text-orange-700 mb-1">⚠️ DBA Setup Required</p>
                            <p className="text-orange-600">
                              Materialized View Logs must be created by your DBA before CDC can capture changes.
                              Example SQL: <code className="bg-white px-1 rounded">CREATE MATERIALIZED VIEW LOG ON schema.table WITH ROWID, PRIMARY KEY, SEQUENCE INCLUDING NEW VALUES;</code>
                            </p>
                          </div>
                        )}
                      </>
                    )}
                    
                    {streamForm.cdc_method === 'datastream' && (
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-xs">
                        <p className="font-medium text-purple-700 mb-1">ℹ️ Network Requirements</p>
                        <p className="text-purple-600">
                          Google Cloud Datastream requires network access from Google Cloud to your Oracle database.
                          Your firewall must allow inbound connections from Google Cloud IP ranges.
                        </p>
                      </div>
                    )}
                    
                    <DialogFooter className="gap-2">
                      <Button type="button" variant="outline" onClick={() => setStreamDialogStep(2)}>
                        Back
                      </Button>
                      <Button type="submit" data-testid="create-stream-submit">
                        Create Stream
                      </Button>
                    </DialogFooter>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </div>

          {streams.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Activity className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg">No CDC Streams</h3>
                <p className="text-muted-foreground text-sm mt-1 text-center max-w-md">
                  {oracleSources.length === 0
                    ? "Create an Oracle source first, then set up a CDC stream"
                    : "Create a CDC stream to start real-time data replication"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {streams.map((stream) => (
                <CDCStreamCard
                  key={stream.id}
                  stream={stream}
                  onStart={handleStartStream}
                  onPause={handlePauseStream}
                  onDelete={handleDeleteStream}
                  onRefresh={handleRefreshStream}
                  onViewMetrics={handleViewMetrics}
                  onPollNow={handlePollNow}
                  loading={actionLoading}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Transformations Tab */}
        <TabsContent value="transformations" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">CDC Transformations</h2>
              <p className="text-sm text-muted-foreground">Silver and Gold layer transformations on CDC data</p>
            </div>
            <Dialog open={transformDialogOpen} onOpenChange={setTransformDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-transformation-btn" disabled={streams.length === 0}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Transformation
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create CDC Transformation</DialogTitle>
                  <DialogDescription>
                    Apply Silver or Gold transformations to CDC replicated data
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateTransformation} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Transformation Name</Label>
                      <Input
                        value={transformForm.name}
                        onChange={(e) => setTransformForm({ ...transformForm, name: e.target.value })}
                        placeholder="e.g., clean_sales_data"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select
                        value={transformForm.transformation_type}
                        onValueChange={(val) => setTransformForm({ 
                          ...transformForm, 
                          transformation_type: val,
                          target_dataset: val === 'silver' ? 'cdc_silver' : 'cdc_gold'
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="silver">Silver (Clean & Cast)</SelectItem>
                          <SelectItem value="gold">Gold (Aggregate & Join)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>CDC Stream</Label>
                    <Select
                      value={transformForm.cdc_stream_id}
                      onValueChange={handleStreamSelectForTransform}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select CDC stream" />
                      </SelectTrigger>
                      <SelectContent>
                        {streams.map((stream) => (
                          <SelectItem key={stream.id} value={stream.id}>
                            {stream.name} ({stream.destination_dataset})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {cdcTables.length > 0 ? (
                    <div className="space-y-2">
                      <Label>Source Table</Label>
                      <Select
                        value={transformForm.source_table ? `${cdcTables.find(t => t.table_id === transformForm.source_table)?.dataset}.${transformForm.source_table}` : ""}
                        onValueChange={handleSourceTableSelect}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select source table" />
                        </SelectTrigger>
                        <SelectContent>
                          {cdcTables.map((table) => (
                            <SelectItem key={table.full_id} value={table.full_id}>
                              {table.full_id} ({(table.num_rows || 0).toLocaleString()} rows)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : transformForm.cdc_stream_id ? (
                    <div className="space-y-2">
                      <Label>Source Table</Label>
                      <Input
                        value={transformForm.source_table}
                        onChange={(e) => setTransformForm({ ...transformForm, source_table: e.target.value })}
                        placeholder="Enter table name manually (e.g., BILLS_MASTER)"
                        required
                      />
                      <p className="text-xs text-muted-foreground">
                        No tables found in CDC dataset. Enter the table name manually.
                      </p>
                    </div>
                  ) : null}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Target Dataset</Label>
                      <Input
                        value={transformForm.target_dataset}
                        onChange={(e) => setTransformForm({ ...transformForm, target_dataset: e.target.value })}
                        placeholder="cdc_silver"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Target Table</Label>
                      <Input
                        value={transformForm.target_table}
                        onChange={(e) => setTransformForm({ ...transformForm, target_table: e.target.value })}
                        placeholder="clean_table_name"
                        required
                      />
                    </div>
                  </div>

                  {selectedTableSchema && (
                    <div className="space-y-2">
                      <Label>Fields ({transformForm.fields.length} selected)</Label>
                      <div className="max-h-40 overflow-y-auto border rounded p-2 space-y-1">
                        {transformForm.fields.map((field, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <span className="font-mono flex-1">{field.source_field}</span>
                            <ArrowRight className="w-3 h-3" />
                            <span className="font-mono flex-1">{field.target_field}</span>
                            <Badge variant="secondary" className="text-xs">{field.data_type}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Filter Condition (optional)</Label>
                    <Input
                      value={transformForm.filter_condition}
                      onChange={(e) => setTransformForm({ ...transformForm, filter_condition: e.target.value })}
                      placeholder="e.g., status = 'ACTIVE'"
                    />
                  </div>

                  {/* Multi-Tenant Configuration */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Multi-Tenant Options
                    </h4>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div>
                          <Label className="font-normal">Tenant-Aware Transformation</Label>
                          <p className="text-xs text-muted-foreground">
                            Include tenant_id column in output and preserve tenant isolation
                          </p>
                        </div>
                        <Switch
                          checked={transformForm.tenant_aware}
                          onCheckedChange={(checked) => setTransformForm({ ...transformForm, tenant_aware: checked })}
                          data-testid="transform-tenant-aware-switch"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Merge Strategy</Label>
                        <Select
                          value={transformForm.tenant_merge_strategy}
                          onValueChange={(val) => setTransformForm({ ...transformForm, tenant_merge_strategy: val })}
                        >
                          <SelectTrigger data-testid="transform-merge-strategy-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="merge_by_tenant">Merge by Tenant (delete + insert per tenant)</SelectItem>
                            <SelectItem value="replace_all">Replace All (truncate entire table)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Merge by Tenant preserves other tenants&apos; data when running for one tenant
                        </p>
                      </div>

                      {transformForm.transformation_type === 'gold' && (
                        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <Label className="font-normal">Auto-Apply RLS Policy</Label>
                            <p className="text-xs text-muted-foreground">
                              Automatically create Row-Level Security policy for this Gold table
                            </p>
                          </div>
                          <Switch
                            checked={transformForm.auto_apply_rls}
                            onCheckedChange={(checked) => setTransformForm({ ...transformForm, auto_apply_rls: checked })}
                            data-testid="transform-auto-rls-switch"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <DialogFooter>
                    <Button 
                      type="submit" 
                      disabled={!transformForm.name || !transformForm.cdc_stream_id || !transformForm.source_table || !transformForm.target_table}
                    >
                      Create Transformation
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {transformations.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Layers className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg">No Transformations</h3>
                <p className="text-muted-foreground text-sm mt-1 text-center max-w-md">
                  {streams.length === 0
                    ? "Create a CDC stream first to enable transformations"
                    : "Create transformations to process CDC data into Silver and Gold layers"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {transformations.map((trans) => (
                <TransformationCard
                  key={trans.id}
                  transformation={trans}
                  onRunClick={handleRunTransformationClick}
                  onDelete={handleDeleteTransformation}
                  loading={actionLoading}
                />
              ))}
            </div>
          )}

          {/* Run Transformation for Tenant Dialog */}
          <Dialog open={runTransformDialogOpen} onOpenChange={setRunTransformDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Run Transformation for Tenant</DialogTitle>
                <DialogDescription>
                  Select which tenant and unit to run this transformation for
                </DialogDescription>
              </DialogHeader>
              
              {selectedTransformation && (
                <div className="space-y-4">
                  {/* Transformation Info */}
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm font-medium">Transformation:</p>
                    <p className="text-lg font-semibold">{selectedTransformation.name}</p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant={selectedTransformation.transformation_type === 'silver' ? 'secondary' : 'default'}>
                        {selectedTransformation.transformation_type.toUpperCase()}
                      </Badge>
                      <Badge variant="outline">
                        {selectedTransformation.target_dataset}.{selectedTransformation.target_table}
                      </Badge>
                    </div>
                  </div>

                  {/* Tenant Selection */}
                  <div className="space-y-2">
                    <Label>Select Tenant *</Label>
                    <Select value={selectedTenantIdForRun} onValueChange={handleTenantSelectForRun}>
                      <SelectTrigger data-testid="run-transform-tenant-select">
                        <SelectValue placeholder="Select tenant..." />
                      </SelectTrigger>
                      <SelectContent>
                        {tenants.map(t => (
                          <SelectItem key={t.tenant_id} value={t.tenant_id}>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4" />
                              <span>{t.name}</span>
                              <span className="text-xs text-muted-foreground">({t.tenant_id})</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Unit Selection - Only show if tenant has units */}
                  {availableUnitsForRun.length > 0 && (
                    <div className="space-y-2">
                      <Label>Select Accounting Unit *</Label>
                      <Select value={selectedUnitIdForRun} onValueChange={setSelectedUnitIdForRun}>
                        <SelectTrigger data-testid="run-transform-unit-select">
                          <SelectValue placeholder="Select unit..." />
                        </SelectTrigger>
                        <SelectContent>
                          {availableUnitsForRun.map(unit => (
                            <SelectItem key={unit.unit_id} value={unit.unit_id}>
                              <div className="flex items-center gap-2">
                                <Database className="w-4 h-4" />
                                <span>{unit.name}</span>
                                <span className="text-xs text-muted-foreground">({unit.unit_id})</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Each unit&apos;s data will be merged separately (not replaced)
                      </p>
                    </div>
                  )}

                  {/* Data Flow Preview */}
                  {selectedTenantIdForRun && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-xs text-green-700 font-medium mb-1">This will:</p>
                      <ul className="text-sm text-green-800 space-y-1">
                        <li>• Process data where <code className="bg-white px-1 rounded">tenant_id = &apos;{selectedTenantIdForRun}&apos;</code>
                          {selectedUnitIdForRun && <> AND <code className="bg-white px-1 rounded">unit_id = &apos;{selectedUnitIdForRun}&apos;</code></>}
                        </li>
                        {selectedTransformation?.tenant_merge_strategy === 'merge_by_tenant' && (
                          <li>• Only replace data for this {selectedUnitIdForRun ? 'unit' : 'tenant'} (other {selectedUnitIdForRun ? 'units' : 'tenants'} preserved)</li>
                        )}
                        {selectedTransformation?.auto_apply_rls && selectedTransformation?.transformation_type === 'gold' && (
                          <li>• Auto-create RLS policy for this tenant</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setRunTransformDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={executeTransformationForTenant} 
                  disabled={!selectedTenantIdForRun || (availableUnitsForRun.length > 0 && !selectedUnitIdForRun)}
                  data-testid="confirm-run-transformation"
                >
                  <Play className="w-4 h-4 mr-1" />
                  Run Transformation
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Sources Tab */}
        <TabsContent value="sources" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">CDC Sources</h2>
              <p className="text-sm text-muted-foreground">Configure which database schemas/tables to replicate (Oracle, SQL Server, PostgreSQL)</p>
            </div>
            <Dialog open={sourceDialogOpen} onOpenChange={handleSourceDialogClose}>
              <DialogTrigger asChild>
                <Button data-testid="create-source-btn" disabled={oracleConnections.length === 0 && sqlserverConnections.length === 0 && postgresqlConnections.length === 0}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add CDC Source
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
                <DialogHeader>
                  <DialogTitle>Add CDC Source</DialogTitle>
                  <DialogDescription>
                    Configure which database schemas and tables to include in CDC replication
                  </DialogDescription>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto pr-2" style={{ maxHeight: 'calc(90vh - 180px)' }}>
                  <form id="add-cdc-source-form" onSubmit={handleCreateSource} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Source Name</Label>
                      <Input
                        value={sourceForm.name}
                        onChange={(e) => setSourceForm({ ...sourceForm, name: e.target.value })}
                        placeholder="e.g., ERP Sales Data"
                        required
                        data-testid="source-name-input"
                      />
                    </div>
                    
                    {/* Source Type Selection */}
                    <div className="space-y-2">
                      <Label>Source Database Type</Label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { id: 'oracle', label: 'Oracle', color: 'orange', conns: oracleConnections },
                        { id: 'sqlserver', label: 'SQL Server', color: 'blue', conns: sqlserverConnections },
                        { id: 'postgresql', label: 'PostgreSQL', color: 'teal', conns: postgresqlConnections }
                      ].map((type) => (
                        <Button
                          key={type.id}
                          type="button"
                          variant={sourceType === type.id ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleSourceTypeChange(type.id)}
                          disabled={type.conns.length === 0}
                          data-testid={`source-type-${type.id}`}
                        >
                          <Database className={`w-4 h-4 mr-1 ${type.id === 'oracle' ? 'text-orange-500' : type.id === 'sqlserver' ? 'text-blue-500' : 'text-teal-500'}`} />
                          {type.label}
                          {type.conns.length === 0 && <span className="ml-1 text-xs">(No connections)</span>}
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{sourceType === 'oracle' ? 'Oracle' : sourceType === 'sqlserver' ? 'SQL Server' : 'PostgreSQL'} Connection</Label>
                    <Select
                      value={sourceForm.connection_id || sourceForm.oracle_connection_id}
                      onValueChange={handleConnectionSelect}
                      required
                    >
                      <SelectTrigger data-testid="source-connection-select">
                        <SelectValue placeholder={`Select ${sourceType === 'oracle' ? 'Oracle' : sourceType === 'sqlserver' ? 'SQL Server' : 'PostgreSQL'} connection`} />
                      </SelectTrigger>
                      <SelectContent>
                        {(sourceType === 'oracle' ? oracleConnections : sourceType === 'sqlserver' ? sqlserverConnections : postgresqlConnections).map((conn) => (
                          <SelectItem key={conn.id} value={conn.id}>
                            {conn.name} ({conn.host}:{conn.port})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Schemas Selection */}
                  {(sourceForm.connection_id || sourceForm.oracle_connection_id) && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Select Schemas ({sourceForm.selectedSchemas.length} selected)</Label>
                        {schemasLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                      </div>
                      {availableSchemas.length > 0 ? (
                        <ScrollArea className="h-32 border rounded-md p-2">
                          <div className="space-y-2">
                            {availableSchemas.map((schema) => (
                              <div key={schema.schema_name} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`schema-${schema.schema_name}`}
                                  checked={sourceForm.selectedSchemas.includes(schema.schema_name)}
                                  onCheckedChange={(checked) => handleSchemaToggle(schema.schema_name, checked)}
                                />
                                <label
                                  htmlFor={`schema-${schema.schema_name}`}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                                >
                                  {schema.schema_name}
                                  <span className="text-muted-foreground ml-2">
                                    ({schema.table_count} tables)
                                  </span>
                                </label>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      ) : !schemasLoading ? (
                        <p className="text-sm text-muted-foreground">No schemas available</p>
                      ) : null}
                    </div>
                  )}

                  {/* Tables Selection */}
                  {sourceForm.selectedSchemas.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>
                          Select Tables ({sourceForm.selectedTables.length} selected)
                          {sourceForm.selectedTables.length === 0 && (
                            <span className="text-muted-foreground ml-2 font-normal">
                              - Leave empty for all tables
                            </span>
                          )}
                        </Label>
                        {tablesLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                      </div>
                      {/* Search filter for tables */}
                      <div className="relative">
                        <Input
                          placeholder="Search tables..."
                          value={tableSearchFilter}
                          onChange={(e) => setTableSearchFilter(e.target.value)}
                          className="h-8 text-sm"
                        />
                        {tableSearchFilter && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-1 top-1 h-6 w-6 p-0"
                            onClick={() => setTableSearchFilter("")}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                      <ScrollArea className="border rounded-md p-2 h-[200px] max-h-[30vh]">
                        <div className="space-y-4 pr-4">
                          {sourceForm.selectedSchemas.map((schemaName) => {
                            const allSchemaTables = availableTables.filter(t => t.owner === schemaName);
                            const schemaTables = tableSearchFilter 
                              ? allSchemaTables.filter(t => 
                                  t.table_name.toLowerCase().includes(tableSearchFilter.toLowerCase())
                                )
                              : allSchemaTables;
                            const selectedInSchema = sourceForm.selectedTables.filter(t => t.startsWith(schemaName + ".")).length;
                            
                            if (tableSearchFilter && schemaTables.length === 0) return null;
                            
                            return (
                              <div key={schemaName} className="space-y-2">
                                <div className="flex items-center justify-between bg-muted px-2 py-1 rounded sticky top-0 z-10">
                                  <span className="font-medium text-sm">{schemaName}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">
                                      {selectedInSchema}/{allSchemaTables.length}
                                      {tableSearchFilter && ` (${schemaTables.length} shown)`}
                                    </span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 text-xs"
                                      onClick={() => handleSelectAllTables(schemaName)}
                                    >
                                      All
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 text-xs"
                                      onClick={() => handleDeselectAllTables(schemaName)}
                                    >
                                      None
                                    </Button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-x-2 gap-y-1 pl-2">
                                  {schemaTables.map((table) => (
                                    <div key={table.fullName} className="flex items-center space-x-1.5">
                                      <Checkbox
                                        id={`table-${table.fullName}`}
                                        checked={sourceForm.selectedTables.includes(table.fullName)}
                                        onCheckedChange={(checked) => handleTableToggle(table.fullName, checked)}
                                        className="h-3.5 w-3.5"
                                      />
                                      <label
                                        htmlFor={`table-${table.fullName}`}
                                        className="text-[11px] leading-none cursor-pointer truncate max-w-[120px]"
                                        title={table.table_name}
                                      >
                                        {table.table_name}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                      <p className="text-xs text-muted-foreground">
                        Leave all unchecked to replicate all tables in selected schemas
                      </p>
                    </div>
                  )}

                  {/* Selected Summary */}
                  {(sourceForm.selectedSchemas.length > 0 || sourceForm.selectedTables.length > 0) && (
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm font-medium">Selection Summary</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {sourceForm.selectedSchemas.map((schema) => (
                          <Badge key={schema} variant="secondary" className="gap-1">
                            {schema}
                            <button
                              type="button"
                              onClick={() => handleSchemaToggle(schema, false)}
                              className="ml-1 hover:text-destructive"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                      {sourceForm.selectedTables.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-2">
                          {sourceForm.selectedTables.length} specific table(s) selected
                        </p>
                      )}
                    </div>
                  )}
                  </form>
                </div>
                <DialogFooter className="border-t pt-4 mt-2">
                  <Button 
                    type="submit"
                    form="add-cdc-source-form"
                    data-testid="create-source-submit"
                    disabled={sourceForm.selectedSchemas.length === 0}
                  >
                    Add Source
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {(oracleSources.length + cdcSources.length) === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Database className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg">No CDC Sources</h3>
                <p className="text-muted-foreground text-sm mt-1 text-center max-w-md">
                  {(oracleConnections.length + sqlserverConnections.length + postgresqlConnections.length) === 0
                    ? "Create a database connection first in the Connections page"
                    : "Add a CDC source to define which schemas and tables to replicate"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Oracle Sources */}
              {oracleSources.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-orange-600 flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    Oracle Sources ({oracleSources.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {oracleSources.map((source) => (
                      <OracleSourceCard key={source.id} source={source} onDelete={handleDeleteSource} />
                    ))}
                  </div>
                </div>
              )}
              
              {/* SQL Server Sources */}
              {cdcSources.filter(s => s.source_type === 'sqlserver').length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-blue-600 flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    SQL Server Sources ({cdcSources.filter(s => s.source_type === 'sqlserver').length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cdcSources.filter(s => s.source_type === 'sqlserver').map((source) => (
                      <CDCSourceCard key={source.id} source={source} onDelete={handleDeleteGenericSource} sourceType="sqlserver" />
                    ))}
                  </div>
                </div>
              )}
              
              {/* PostgreSQL Sources */}
              {cdcSources.filter(s => s.source_type === 'postgresql').length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-teal-600 flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    PostgreSQL Sources ({cdcSources.filter(s => s.source_type === 'postgresql').length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cdcSources.filter(s => s.source_type === 'postgresql').map((source) => (
                      <CDCSourceCard key={source.id} source={source} onDelete={handleDeleteGenericSource} sourceType="postgresql" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Metrics Dialog */}
      <MetricsDialog
        stream={selectedStream}
        metrics={streamMetrics}
        tables={streamTables}
        open={metricsDialogOpen}
        onClose={() => setMetricsDialogOpen(false)}
        loading={metricsLoading}
      />
    </div>
  );
}
